import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import {
  ADD_CANNED_MESSAGES_FORM_FIELDS,
  CANNED_MESSAGES_FILTER_COLUMNS,
  CANNED_MESSAGES_TABLE_HEADERS,
  EDIT_CANNED_MESSAGES_FORM_FIELDS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  successAlert,
  transformCannedMessageResponseRow,
  transformCannedMessagesRows,
  transformCannedMessageUpdateBody,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import TableActionButton from "../ui/TableActionButton";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import {
  deleteCannedMessage,
  getCannedMessageByRef,
  updateCannedMessageByRef,
} from "../api";
import EditRow from "../components/dataTable/EditRow";
import { INITIAL_CANNED_MESSAGES_EDIT_STATE } from "../constants/editState";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  DELETE_CANNED_MESSAGE_TRANSLATIONS,
  READ_CANNED_MESSAGE_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import SearchContext from "../store/searchContext";
import BulkUpload from "../components/bulkUpload/BulkUpload";

interface ISearchData {
  "MSG-ID"?: string | null;
  "CANNED-TEXT"?: string | null;
}

const INITIAL_STATE = {
  "MSG-ID": "",
  "CANNED-TEXT": "",
};

const searchFields = ["MSG-ID", "CANNED-TEXT"];

const searchConfigNew = createSearchConfig(
  searchFields,
  ADD_CANNED_MESSAGES_FORM_FIELDS
);

const CannedMessages = () => {
  const {
    token,
    scopes: { lmr_msgs: lmrMsgs },
  } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);

  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  useAppTitle("Canned Messages- IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [messages, setMessages] = useState<any[]>([]);

  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const fetchCannedMessages = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize, sortByField, sortDirection } =
          searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(sortByField && { sortByField }),
          ...(sortDirection && { sortDirection }),
          ...(data &&
            data["MSG-ID"] && {
              "MSG-ID": data["MSG-ID"],
            }),
          ...(data &&
            data["CANNED-TEXT"] && {
              "CANNED-TEXT": data["CANNED-TEXT"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/lmr-msgs?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setMessages(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
      sortByField: undefined,
      sortDirection: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchCannedMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  const deleteConnection = async (ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteCannedMessage({
          token,
          org: selectedOrg.id,
          ref,
        });
        successAlert(DELETE_CANNED_MESSAGE_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchCannedMessages();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };

  return (
    <>
      <SectionHeader
        pageTitle={READ_CANNED_MESSAGE_TRANSLATIONS.header}
        pageDescription={READ_CANNED_MESSAGE_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfigNew}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          {showSidePanel &&
          selectedOrg &&
          selectedConnectionId &&
          !isBulkUploadEnabled ? (
            <EditRow
              panelName={READ_CANNED_MESSAGE_TRANSLATIONS.editRow.panelName}
              initialState={INITIAL_CANNED_MESSAGES_EDIT_STATE}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              onDelete={() => deleteConnection(selectedConnectionId)}
              formFields={EDIT_CANNED_MESSAGES_FORM_FIELDS}
              header={READ_CANNED_MESSAGE_TRANSLATIONS.editRow.header}
              showSmallSidepanel
              transformResponseRow={transformCannedMessageResponseRow}
              transformUpdateBody={transformCannedMessageUpdateBody}
              onUpdate={updateCannedMessageByRef}
              loadDataByRef={getCannedMessageByRef}
              hasDeletePermission={lmrMsgs && lmrMsgs.includes("d")}
              hasEditPermission={lmrMsgs && lmrMsgs.includes("u")}
              refreshTable={fetchCannedMessages}
              deleteModalItemName="MSG-ID"
            />
          ) : null}

          {showSidePanel && isBulkUploadEnabled ? (
            <BulkUpload
              moduleName="Canned Message"
              pathName="/api/v1/lmr-msgs"
              csvTemplateFile={["MESSAGE-ID,CANNED-TEXT"]}
              closeBulkUploadSidePanel={() => {
                handleToggleSidePanel();
                setIsBulkUploadEnabled(false);
                fetchCannedMessages();
              }}
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            filterColumns={CANNED_MESSAGES_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            showCreateButton={lmrMsgs && lmrMsgs.includes("c")}
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/cannedMessages/new`);
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rows={transformCannedMessagesRows(messages).map((row: any) => ({
              ...row,
              action: (
                <TableActionButton
                  onActionButtonClick={() => {
                    setSelectedConnectionId(row.id);
                    handleToggleSidePanel();
                  }}
                />
              ),
            }))}
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            headers={CANNED_MESSAGES_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
            sortByField={searchQuery.sortByField}
            sortDirection={searchQuery.sortDirection}
            handleSort={sortTable}
            showBulkUploadButton={lmrMsgs && lmrMsgs.includes("c")}
            onBulkUploadButtonClick={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(true);
            }}
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_CANNED_MESSAGE_TRANSLATIONS.infoAlert.heading}
          subText={READ_CANNED_MESSAGE_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default CannedMessages;
